import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControllerMultiTextArea } from '@components/forms/invite-guest/controller-multi-text-area';
import { MultiOfferHeader } from '@components/forms/invite-guest/multi-offer-header';
import { Loader } from '@components/loader';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';

export interface BulkHolderInfoProps {
  onContinue: () => void;
  handleClose: () => void;
  isLoading: boolean;
  isNominative?: boolean;
  triggerValidation?: (fieldName?: string) => Promise<boolean>;
}

export const BulkHolderInfo = ({
  onContinue,
  handleClose,
  isLoading,
  isNominative = true,
  triggerValidation,
}: BulkHolderInfoProps) => {
  const { t } = useTranslation();
  const {
    formState: { isValid },
    watch,
  } = useFormContext();

  const [isValidationTriggered, setIsValidationTriggered] = useState(false);
  const bulkHolderText = watch('bulkHolderText') as string;

  // Enhanced validation trigger that tracks validation state
  const handleTriggerValidation = useCallback(
    async (fieldName?: string) => {
      if (triggerValidation) {
        const result = await triggerValidation(fieldName);
        setIsValidationTriggered(true);
        return result;
      }
      return false;
    },
    [triggerValidation]
  );

  const handleContinue = async () => {
    // If manual validation is enabled and hasn't been triggered yet, trigger it first
    if (triggerValidation && !isValidationTriggered && bulkHolderText?.trim()) {
      const isFormValid = await handleTriggerValidation();
      if (isFormValid) {
        onContinue();
      }
      return;
    }

    // If validation was already triggered or no manual validation, proceed normally
    onContinue();
  };

  if (isLoading) {
    return (
      <div className='relative max-h-[80vh] overflow-y-auto'>
        <Loader />
      </div>
    );
  }

  const textAreaPlaceholder = isNominative
    ? 'John Doe johndoe@example.com\nSarah Ross sarahross@example.com\nLesedi Khumalo lesedikhu@example.com\nJavier Rodriguez Garcia j.rodrigeuzg@example.com'
    : 'John Doe 3 johndoe@example.com\nSarah Ross +2 sarahross@example.com\nLesedi Khumalo lesedikhu@example.com\nJavier Rodriguez Garcia 2 j.rodrigeuzg@example.com';

  const isSubmitDisabled = !isValid;

  return (
    <div className='relative max-h-[80vh] overflow-y-auto'>
      <ModalStickyHeader title={t('passes:addBulkHoldersTitle')} />
      <div className='flex flex-col gap-4 px-4 pb-6 sm:px-20 sm:pb-12'>
        <MultiOfferHeader
          instructionsDescription={t('passes:bulkHolderListInstructionsText')}
          instructionsTitle={t('passes:bulkHolderListInstructionsTitle')}
          formName='bulkHolderText'
        />
        <ControllerMultiTextArea
          textareaPlaceholder={textAreaPlaceholder}
          formName='bulkHolderText'
          enforceQuantityOne={isNominative}
          enforceEmailRequired={true}
          triggerValidation={handleTriggerValidation}
        />
      </div>

      <ModalStickyFooter
        primaryButtonText={t('passes:addBulkHoldersButton')}
        onPrimaryButtonClick={handleContinue}
        onSecondaryButtonClick={handleClose}
        secondaryButtonText={t('common:discard')}
        isSubmitDisabled={isSubmitDisabled}
      />
    </div>
  );
};
